import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Checkbox, CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { DropdownOption } from 'components/atoms/Dropdown/Dropdown';
import { compact, difference, filter, find, includes, map, union, uniq } from 'es-toolkit/compat';
import React, { useState } from 'react';
import { ProcedureFilterProps } from '../MainFilters';

interface Props extends Partial<ProcedureFilterProps> {
  options: DropdownOption[];
  label?: string;
  limitTags?: number;
  handleChange: (value: string[]) => void;
  value: Array<string | number>;
  loading?: boolean;
  allowCustomValues?: boolean;
  getSearchText?: (option: DropdownOption) => string;
  inputType?: string;
  inputAttributes?: Record<string, string | boolean | number | undefined>;
  renderTags?: (value: Array<DropdownOption | string | number>, getTagProps: any) => React.ReactNode;
}

const icon = <CheckBoxOutlineBlankIcon />;
const checkedIcon = <CheckBoxIcon />;

const buildCustomOption = (customValue: string): DropdownOption => ({
  value: customValue,
  text: customValue,
});

type AutocompleteFilterOptionValue = DropdownOption | string | number | null | undefined;

const getOptionText = (option: DropdownOption | string | number) =>
  typeof option === 'string' || typeof option === 'number' ? String(option) : option?.text || '';

const getOptionValue = (option: DropdownOption | string | number) =>
  typeof option === 'string' || typeof option === 'number' ? option : option?.value ?? option?.text ?? '';

export const normalizeAutocompleteFilterValue = (option: AutocompleteFilterOptionValue) => {
  if (option === null || option === undefined) {
    return '';
  }

  return String(getOptionValue(option)).trim();
};

const findOptionByNormalizedValue = (options: DropdownOption[], normalizedValue: string) =>
  find(options, (option) => normalizeAutocompleteFilterValue(option) === normalizedValue);

const getDefaultSearchText = (option: DropdownOption) => [option?.text, option?.value].filter(Boolean).join(' ');

const AutocompleteFilter = ({
  label,
  options,
  limitTags = 10,
  handleChange,
  value,
  loading,
  allowCustomValues = false,
  getSearchText,
  inputType,
  inputAttributes,
  renderTags,
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const resolveSearchText = (option: DropdownOption) => getSearchText?.(option) || getDefaultSearchText(option);
  const normalizedSelectedValues = uniq(compact(map(value, normalizeAutocompleteFilterValue)));
  const normalizedValueOptions = compact(
    map(normalizedSelectedValues, (selectedValue) => {
      const trimmedValue = normalizeAutocompleteFilterValue(selectedValue);

      if (!trimmedValue) {
        return null;
      }

      return (
        findOptionByNormalizedValue(options, trimmedValue) ||
        (allowCustomValues ? buildCustomOption(trimmedValue) : null)
      );
    })
  );

  const onCheck = (event: React.ChangeEvent<HTMLInputElement>, option: DropdownOption | string) => {
    const curValue = normalizeAutocompleteFilterValue(option);
    const newChecked = includes(normalizedSelectedValues, curValue)
      ? difference(normalizedSelectedValues, [curValue])
      : union(normalizedSelectedValues, [curValue]);
    handleChange(newChecked);
  };

  return (
    <Autocomplete
      multiple
      freeSolo={allowCustomValues}
      autoHighlight={allowCustomValues}
      limitTags={limitTags}
      clearOnBlur={false}
      sx={{
        '&.MuiAutocomplete-root': {
          '& .MuiButtonBase-root': { maxWidth: '180px' },
          '& .MuiAutocomplete-inputRoot': {
            flexWrap: 'nowrap',
          },
          '& .MuiAutocomplete-tag': {
            whiteSpace: 'nowrap',
          },
        },
      }}
      size="small"
      value={normalizedValueOptions}
      disableCloseOnSelect
      blurOnSelect={false}
      options={options}
      getOptionLabel={(option) => getOptionText(option)}
      renderTags={renderTags}
      onChange={(event: any, newValue: any | null) => {
        const selectedValues = uniq(
          compact(
            map(newValue, (item) => {
              const normalizedValue = normalizeAutocompleteFilterValue(item);
              return normalizedValue || undefined;
            })
          )
        );

        handleChange(selectedValues);
        setInputValue('');
      }}
      inputValue={inputValue}
      renderOption={(props, option, { selected }) => {
        return (
          <li {...props} style={{ paddingTop: 0, paddingBottom: 0 }} key={getOptionValue(option)}>
            <Checkbox
              size="small"
              checked={selected}
              icon={icon}
              checkedIcon={checkedIcon}
              onChange={(e) => onCheck(e, option)}
            />
            {getOptionText(option)}
          </li>
        );
      }}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'clear') {
          handleChange([]);
        } else {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={(availableOptions) => {
        const filteredOptions = filter(availableOptions, (item) =>
          includes(resolveSearchText(item).toLowerCase(), inputValue.toLowerCase())
        );
        const trimmedInputValue = inputValue.trim();

        if (!allowCustomValues || !trimmedInputValue) {
          return filteredOptions;
        }

        const alreadyIncluded = Boolean(
          [...normalizedValueOptions, ...filteredOptions].find((item) =>
            includes(
              (typeof item === 'string' ? item : resolveSearchText(item)).toLowerCase(),
              trimmedInputValue.toLowerCase()
            )
          )
        );

        return alreadyIncluded ? filteredOptions : [...filteredOptions, buildCustomOption(trimmedInputValue)];
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          type={inputType}
          autoComplete={typeof inputAttributes?.autoComplete === 'string' ? inputAttributes.autoComplete : undefined}
          InputLabelProps={{
            ...params.InputLabelProps,
            sx: {
              whiteSpace: 'nowrap',
            },
          }}
          inputProps={{
            ...params.inputProps,
            ...inputAttributes,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default AutocompleteFilter;
